import * as React from "react";
import { ToastContainer } from "react-toastify";

import { connect } from "react-redux";
import axios from "axios";

import { Container } from "@mui/material";

import { ThunkDispatch } from "redux-thunk";
import { Action } from "redux";

import Breadcrumbs from "@teamprevent/common/Breadcrumbs";

import { ErrorBoundary } from "@teamprevent/common/ErrorBoundary";

import { ReadonlyCustomReferralForm } from "@teamprevent/common/questionnaires/components/referralForm/ReadonlyCustomReferralForm";
import { CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";

import { CaseType } from "@teamprevent/common/model";
import { TermsAndConditionsAcceptanceModal } from "@teamprevent/common/termsAndConditions/TermsAndConditionsAcceptanceModal";
import { TermsAndConditionsOfUseConfig } from "@teamprevent/common/termsAndConditions/model";

import { Header } from "./global/Header";
import Footer from "./global/Footer";
import { Base as CustomerUsers } from "./crud/customerUser";
import { Base as OhCase } from "./crud/ohCase";
import { ApplicationState } from "./applicationState";

import { Base as ActionableTasks } from "./actionableTask/components/Base";
import { Dashboard } from "./dashboard";

import { WritablePpqForm } from "./Ppq/components/WritablePpqForm";
import { StatisticsOverview } from "./statistics";
import { WritableReferralForm } from "./referral/components/WritableReferralForm";
import { WritablePhysiotherapyForm } from "./physiotherapy/components/WritablePhysiotherapyForm";
import { Base as Documents } from "./documents";
import { Base as AssessmentAndWellBeing } from "./assessmentAndWellBeing";
import { CurrentUser } from "./auth";
import { anySraRole, canRequestOtherServices } from "./auth/model";
import { AllowedAssessmentAndWellBeingFeatures } from "./assessmentAndWellBeing/models";
import { WritableStressRiskAssessmentForm } from "./assessmentAndWellBeing/components/WritableStressRiskAssessmentForm";
import { MyResults } from "./assessmentAndWellBeing/components/MyResults";
import { ManagerSraSummary } from "./assessmentAndWellBeing/components/ManagerSraSummary";
import { SraSurveyResults } from "./assessmentAndWellBeing/components/SraSurveyResults";
import { updateTermsAccepted } from "./auth";
import { QuestionnaireConfigApi } from "./questionnaireConfig/QuestionnaireConfigApi";
import { LoginPage } from "./auth/login";
import { CustomerIdMismatchErrorModal } from "./global/CustomerIdMismatchErrorModal";
import { WritableGenericCaseForm } from "./genericCase/components/WritableGenericCaseForm";

import { Fragment, Location } from "redux-little-router";

interface AppStateProps {
    allowedAssessmentAndWellBeingFeatures: string[];
    currentUser: CurrentUser;
}

interface AppDispatchProps {
    updateTermsAccepted: () => void;
}

const AppUnconnected: React.FunctionComponent<AppStateProps & AppDispatchProps>
= ({ allowedAssessmentAndWellBeingFeatures, currentUser, updateTermsAccepted: updateTermsAcceptedProp }: AppStateProps & AppDispatchProps) => {
    const [termsAndConditionsAcceptanceModalOpen, setTermsAndConditionsAcceptanceModalOpen] = React.useState(false);
    const [termsAndConditionsAcceptanceModalShown, setTermsAndConditionsAcceptanceModalShown] = React.useState(false);
    const [termsContent, setTermsContent] = React.useState<string>("");

    React.useEffect(() => {
        (async () => {
            if (!termsAndConditionsAcceptanceModalShown && currentUser.termsAcceptanceRequired) {
                const termsAcceptanceConfig = await (new QuestionnaireConfigApi(axios)).get(TermsAndConditionsOfUseConfig);
                if (termsAcceptanceConfig && termsAcceptanceConfig.questions?.length > 0) {
                    const consentText = (termsAcceptanceConfig.questions[0] as CustomQuestionConfig).questionText;
                    setTermsContent(consentText);
                    setTermsAndConditionsAcceptanceModalOpen(true);
                    setTermsAndConditionsAcceptanceModalShown(true);
                }
            }
        })();
    }, [currentUser, termsAndConditionsAcceptanceModalShown]);

    const onAccept = React.useCallback((accepted: boolean) => {
        if (!accepted) {
            const redirectUrl = window.location.origin + "/account/signout";
            window.location.replace(redirectUrl);
        }
        else {
            updateTermsAcceptedProp();
        }
    }, [updateTermsAcceptedProp]);

    const closeAndRejectTermsAndConditions = React.useCallback(() => setTermsAndConditionsAcceptanceModalOpen(false), []);

    const referralCondition = React.useCallback((l: Location) => l.params?.ohCaseId !== undefined, []);

    const otherServicesCondition = React.useCallback(() => canRequestOtherServices(currentUser), [currentUser]);

    if (location.pathname === "/login")
    {
        return (
            <Container>
                <LoginPage />
            </Container>
        );
    }

    return (
        <div className="global-wrapper">
            <Header />
            <ToastContainer
                closeButton={false}
                autoClose={5000}
                hideProgressBar={true}
            />
            <CustomerIdMismatchErrorModal />
            <div className="content-wrapper">
                <Container className="main-content" maxWidth={false}>
                    <TermsAndConditionsAcceptanceModal
                        open={termsAndConditionsAcceptanceModalOpen}
                        onClose={closeAndRejectTermsAndConditions}
                        onAccept={onAccept}
                        termsContent={termsContent}
                    />
                    <Fragment forRoute="/" >
                        <ErrorBoundary>
                            <Breadcrumbs />
                            <Fragment forRoute="/referral">
                                <WritableReferralForm />
                            </Fragment>
                            <Fragment forRoute="/customerUsers">
                                <CustomerUsers />
                            </Fragment>
                            <Fragment forRoute="/ohCases">
                                <OhCase />
                            </Fragment>
                            <Fragment forRoute="/workadjustments">
                                <OhCase include={CaseType.WORK_ADJUSTMENT} />
                            </Fragment>
                            <Fragment forRoute="/actions/:ohCaseId/:taskId/referral" withConditions={referralCondition}>
                                <ReadonlyCustomReferralForm api="/api/case/matchedReferral" documentBaseApi="/api/referral" />
                            </Fragment>
                            <Fragment forRoute="/actions">
                                <ActionableTasks />
                            </Fragment>
                            <Fragment forRoute="/ppq">
                                <WritablePpqForm />
                            </Fragment>
                            <Fragment forRoute="/statistics">
                                <StatisticsOverview />
                            </Fragment>
                            <Fragment forRoute="/physiotherapy">
                                <WritablePhysiotherapyForm />
                            </Fragment>
                            <Fragment forRoute="/otherServices" withConditions={otherServicesCondition}>
                                <WritableGenericCaseForm />
                            </Fragment>
                            <Fragment forRoute="/documents">
                                <Documents />
                            </Fragment>
                            {anySraRole(currentUser) &&
                                allowedAssessmentAndWellBeingFeatures?.indexOf(AllowedAssessmentAndWellBeingFeatures.SRA) > -1 &&
                                <>
                                    <Fragment forRoute="/assessmentandwellbeing/newstressriskassessment">
                                        <WritableStressRiskAssessmentForm />
                                    </Fragment>
                                    <Fragment forRoute="/assessmentandwellbeing/myresultsForCase/:caseId">
                                        <ManagerSraSummary />
                                    </Fragment>
                                    <Fragment forRoute="/assessmentandwellbeing/myresults/:sraEmployeeSectionId">
                                        <ManagerSraSummary />
                                    </Fragment>
                                    <Fragment forRoute="/assessmentandwellbeing/myresults">
                                        <MyResults />
                                    </Fragment>
                                    <Fragment forRoute="/assessmentandwellbeing/overview">
                                        <SraSurveyResults />
                                    </Fragment>
                                    <Fragment forRoute="/assessmentandwellbeing">
                                        <AssessmentAndWellBeing />
                                    </Fragment>
                                </>
                            }
                            <Fragment forNoMatch>
                                <Dashboard />
                            </Fragment>
                        </ErrorBoundary>
                    </Fragment>
                </Container>
            </div>
            <Footer />
        </div>
    );
};

const mapStateToProps = (state: ApplicationState): AppStateProps => ({
    allowedAssessmentAndWellBeingFeatures: state.allowedAssessmentAndWellBeingFeatures,
    currentUser: state.currentUser
});

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => ({
    updateTermsAccepted: () => dispatch(updateTermsAccepted())
});

export const App = connect(mapStateToProps, mapDispatchToProps)(AppUnconnected);
